import React, { useContext, useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { Globe, ChevronDown } from 'lucide-react'
import { LanguageContext } from '../../i18n/LanguageContext'

const LANGUAGES = [
  { code: 'it', label: 'Italiano' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' }
]

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
`

const Trigger = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0 0.625rem;
  height: 32px;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: 6px;
  background: transparent;
  color: ${props => props.theme.colors.text.secondary};
  font-size: 0.75rem;
  font-weight: 500;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;

  &:hover {
    border-color: ${props => props.theme.colors.text.primary};
    color: ${props => props.theme.colors.text.primary};
  }

  &:focus {
    outline: none;
  }

  svg.chevron {
    transition: transform 0.15s ease;
    transform: ${props => props.$open ? 'rotate(180deg)' : 'rotate(0deg)'};
    opacity: 0.6;
  }
`

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: ${props => props.theme.colors.background.primary};
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  z-index: 200;
  min-width: 130px;
`

const Option = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.5rem 0.875rem;
  background: ${props => props.$active ? props.theme.colors.background.secondary : 'transparent'};
  border: none;
  color: ${props => props.$active ? props.theme.colors.text.primary : props.theme.colors.text.secondary};
  font-size: 0.8125rem;
  font-weight: ${props => props.$active ? '600' : '400'};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  cursor: pointer;
  transition: background 0.1s ease;
  text-align: left;

  &:hover {
    background: ${props => props.theme.colors.background.secondary};
    color: ${props => props.theme.colors.text.primary};
  }
`

const ActiveDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${props => props.theme.colors.text.primary};
  flex-shrink: 0;
`

const LanguageSelectorButtons = () => {
  const { language, setLanguage } = useContext(LanguageContext)
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const handleSelect = (code) => {
    setLanguage(code)
    setOpen(false)
  }

  return (
    <Wrapper ref={wrapperRef}>
      <Trigger
        $open={open}
        onClick={() => setOpen(o => !o)}
        aria-haspopup='listbox'
        aria-expanded={open}
      >
        <Globe size={14} />
        <span>{language.toUpperCase()}</span>
        <ChevronDown size={12} className='chevron' />
      </Trigger>

      {open && (
        <Dropdown role='listbox'>
          {LANGUAGES.map(lang => (
            <Option
              key={lang.code}
              $active={language === lang.code}
              onClick={() => handleSelect(lang.code)}
              role='option'
              aria-selected={language === lang.code}
            >
              {lang.label}
              {language === lang.code && <ActiveDot />}
            </Option>
          ))}
        </Dropdown>
      )}
    </Wrapper>
  )
}

export default LanguageSelectorButtons
